import React from "react";
import {
    Box,
    SimpleGrid,
    Flex,
    Text,
    Heading,
    Icon,
    VStack,
    HStack,
    Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
    ShoppingBag,
    CarFront,
    Hotel,
    Package,
    MapPinned,
    Plane,
    Settings,
    User2,
    Boxes,
    ClipboardList,
    ChevronRight,
    Wrench,
    Route,
    Search,
    LayoutGrid,
} from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

type ExploreItem = {
    id: number;
    title: string;
    subtitle: string;
    path: string;
    icon: React.ElementType;
    color: string;
};

const hexToRgba = (hex: string, alpha: number) => {
    const cleaned = hex.replace("#", "");
    if (cleaned.length !== 6) return `rgba(100,116,139,${alpha})`;

    const bigint = parseInt(cleaned, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ExplorePage = () => {
    const navigate = useNavigate();
    const { theme } = useCustomTheme();

    const isDark = theme.name === "dark";

    const brand = theme.brand || {
        electricBlue: "#27b7ff",
        deepSlate: "#2a2c3b",
        deepInk: "#1a1c2f",
        slate2: "#2e3143",
        softLight: "#eaebf0",
        violet: "#b05cff",
        indigo: "#6f7cff",
    };

    const gradients = theme.gradients || {
        pageBg: theme.background,
        shellBg: theme.surface,
        primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
        primaryButtonHover: `linear-gradient(135deg, #1eaef3 0%, #2cc2ff 100%)`,
        secondaryButton: `linear-gradient(135deg, ${brand.indigo} 0%, ${brand.electricBlue} 100%)`,
        secondaryButtonHover: `linear-gradient(135deg, #6170ff 0%, #20b8ff 100%)`,
        tabHikesSelected: `linear-gradient(135deg, rgba(39,183,255,0.14) 0%, rgba(73,209,255,0.08) 100%)`,
        tabDeliverySelected: `linear-gradient(135deg, rgba(111,124,255,0.14) 0%, rgba(39,183,255,0.08) 100%)`,
        tabShopSelected: `linear-gradient(135deg, rgba(176,92,255,0.14) 0%, rgba(39,183,255,0.08) 100%)`,
    };

    const pageBg = gradients.pageBg || theme.background;
    const shellBg = gradients.shellBg || theme.surface;
    const cardBg = theme.surface;
    const softBg = theme.surfaceAlt;
    const textColor = theme.text;
    const mutedText = theme.textMuted;
    const subtleText = theme.textSubtle;
    const panelShadow = theme.shadowMd;
    const softShadow = theme.shadowSm;
    const cardsBorderRadii = "8px";

    const exploreItems: ExploreItem[] = [
        { id: 1, title: "Shop", subtitle: "Marketplace products", path: "/shop", icon: ShoppingBag, color: "#14B8A6" },
        { id: 2, title: "Products", subtitle: "All listed items", path: "/products", icon: Boxes, color: "#10B981" },
        { id: 3, title: "Hikes", subtitle: "Search trips", path: "/hike", icon: Route, color: "#F59E0B" },
        // { id: 4, title: "Travel", subtitle: "Flights and services", path: "/travel", icon: Plane, color: "#2563EB" },
        { id: 5, title: "Car Hire", subtitle: "Hire vehicles", path: "/hire/cars", icon: CarFront, color: "#8B5CF6" },
        { id: 6, title: "Stay", subtitle: "Accommodation booking", path: "/book/accommodation", icon: Hotel, color: "#EC4899" },
        { id: 7, title: "Delivery", subtitle: "Send packages", path: "/delivery/request", icon: Package, color: "#F97316" },
        { id: 8, title: "Orders", subtitle: "Order tracking", path: "/orders", icon: ClipboardList, color: "#7C3AED" },
        // { id: 9, title: "Locations", subtitle: "Saved places", path: "/locations", icon: MapPinned, color: "#06B6D4" },
        // { id: 10, title: "Car Parts", subtitle: "VIN and catalog", path: "/parts", icon: Wrench, color: "#0EA5E9" },
        { id: 11, title: "Profile", subtitle: "Your account", path: "/profile", icon: User2, color: "#6366F1" },
        { id: 12, title: "Settings", subtitle: "Preferences", path: "/settings", icon: Settings, color: "#64748B" },
    ];

    return (
        <Box h="100%" minH="100%" w="100%" bg={pageBg}>
            <VStack h="100%" minH="100%" w="100%" spacing={0} align="stretch">
                <Box
                    flex="1"
                    minH={0}
                    w="100%"
                    borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
                    bg={shellBg}
                    boxShadow={panelShadow}
                    p={{ base: 3, md: 4 }}
                    backdropFilter={isDark ? "blur(10px)" : undefined}
                >
                    <Box
                        borderRadius={cardsBorderRadii}
                        bg={cardBg}
                        boxShadow={softShadow}
                        border={`1px solid ${theme.border}`}
                        p={{ base: 4, md: 5 }}
                        mb={4}
                        position="relative"
                        overflow="hidden"
                    >
                        <Flex
                            direction={{ base: "column", md: "row" }}
                            justify="space-between"
                            align={{ base: "flex-start", md: "center" }}
                            gap={4}
                        >
                            <Box>
                                <HStack spacing={2} mb={2} flexWrap="wrap">
                                    <Badge
                                        px={3}
                                        py={1}
                                        borderRadius={cardsBorderRadii}
                                        bg={isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"}
                                        color={isDark ? "blue.200" : "blue.700"}
                                        fontWeight="700"
                                    >
                                        Explore
                                    </Badge>
                                    <Badge
                                        px={3}
                                        py={1}
                                        borderRadius={cardsBorderRadii}
                                        bg={isDark ? "rgba(176,92,255,0.14)" : "rgba(176,92,255,0.08)"}
                                        color={isDark ? "purple.200" : "purple.700"}
                                        fontWeight="700"
                                    >
                                        Services
                                    </Badge>
                                </HStack>

                                <Heading size="lg" color={textColor}>
                                    Discover all PickDengu services
                                </Heading>
                                <Text mt={2} color={mutedText} fontWeight="500" maxW="760px">
                                    Browse every service offered on the platform and jump straight into the one you need.
                                </Text>
                            </Box>

                            <HStack
                                spacing={2}
                                px={3}
                                py={2}
                                borderRadius="5px"
                                border={`1px solid ${theme.border}`}
                                bg={isDark ? "rgba(255,255,255,0.03)" : softBg}
                                boxShadow={softShadow}
                            >
                                <Box
                                    w="8px"
                                    h="8px"
                                    borderRadius={cardsBorderRadii}
                                    bg={brand.electricBlue}
                                />
                                <Text color={textColor} fontWeight="700" fontSize="sm">
                                    {exploreItems.length} services
                                </Text>
                            </HStack>
                        </Flex>
                    </Box>

                    <SimpleGrid
                        columns={{ base: 2, sm: 2, md: 3, lg: 4, xl: 6 }}
                        spacing={{ base: 3, md: 3 }}
                    >
                        {exploreItems.map((item) => (
                            <Box
                                key={item.id}
                                role="group"
                                cursor="pointer"
                                onClick={() => navigate(item.path)}
                                borderRadius={cardsBorderRadii}
                                bg={cardBg}
                                boxShadow={softShadow}
                                border={`1px solid ${theme.border}`}
                                p={{ base: 3, md: 3.5 }}
                                transition="all 0.25s ease"
                                _hover={{
                                    transform: "translateY(-3px)",
                                    boxShadow: panelShadow,
                                    borderColor: hexToRgba(item.color, 0.34),
                                    bg: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.98)",
                                }}
                                minH="108px"
                            >
                                <Flex align="center" justify="space-between" mb={2.5}>
                                    <Flex
                                        w="44px"
                                        h="44px"
                                        borderRadius={cardsBorderRadii}
                                        align="center"
                                        justify="center"
                                        bg={hexToRgba(item.color, isDark ? 0.18 : 0.12)}
                                        border={`1px solid ${hexToRgba(item.color, isDark ? 0.30 : 0.22)}`}
                                        boxShadow={softShadow}
                                    >
                                        <Icon
                                            as={item.icon}
                                            boxSize={5}
                                            color={item.color}
                                            fill="transparent"
                                            strokeWidth={2.1}
                                        />
                                    </Flex>

                                    <Flex
                                        w="28px"
                                        h="28px"
                                        borderRadius={cardsBorderRadii}
                                        align="center"
                                        justify="center"
                                        bg={isDark ? "rgba(255,255,255,0.04)" : softBg}
                                        _groupHover={{
                                            bg: hexToRgba(item.color, isDark ? 0.16 : 0.10),
                                        }}
                                        transition="all 0.2s ease"
                                    >
                                        <Icon as={ChevronRight} boxSize={4} color={item.color} />
                                    </Flex>
                                </Flex>

                                <Text
                                    color={textColor}
                                    fontWeight="800"
                                    fontSize="sm"
                                    noOfLines={1}
                                >
                                    {item.title}
                                </Text>

                                <Text
                                    color={mutedText}
                                    fontSize="xs"
                                    mt={1}
                                    noOfLines={2}
                                    lineHeight="short"
                                >
                                    {item.subtitle}
                                </Text>
                            </Box>
                        ))}
                    </SimpleGrid>

                    <Box
                        mt={4}
                        borderRadius={cardsBorderRadii}
                        bg={cardBg}
                        boxShadow={softShadow}
                        border={`1px solid ${theme.border}`}
                        p={{ base: 4, md: 4 }}
                    >
                        <HStack spacing={3}>
                            <Flex
                                w="42px"
                                h="42px"
                                borderRadius={cardsBorderRadii}
                                align="center"
                                justify="center"
                                bg={isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"}
                                border={`1px solid ${isDark ? "rgba(39,183,255,0.22)" : "rgba(39,183,255,0.16)"}`}
                            >
                                <Icon as={Search} boxSize={5} color={brand.electricBlue} />
                            </Flex>
                            <Box>
                                <Text color={textColor} fontWeight="700">
                                    Fast navigation
                                </Text>
                                <Text color={subtleText} fontSize="sm">
                                    Tap any card to open that service page directly.
                                </Text>
                            </Box>
                        </HStack>
                    </Box>
                </Box>
            </VStack>
        </Box>
    );
};

export default ExplorePage;